import { Button, EmptyState } from "@oysterhr/ui"
import { captureException } from "@sentry/react"
import { Component, Suspense, SuspenseProps } from "react"
import { config } from "@oysterhr/core"

export type Props = SuspenseProps & {
  hasError?: boolean
}

export function LazyLoadingSuspenseWithErrorBoundary({ children, fallback, hasError }: Props) {
  return (
    <LazyLoadingErrorBoundary fallback={fallback} hasError={hasError}>
      <Suspense fallback={fallback}>{children}</Suspense>
    </LazyLoadingErrorBoundary>
  )
}

class LazyLoadingErrorBoundary extends Component<Props, { hasError: boolean }> {
  state = { hasError: false }
  constructor(props: Props) {
    const { hasError = false } = props
    super(props)
    this.state.hasError = hasError
  }

  static getDerivedStateFromError(error: Error) {
    if (
      error.message.includes("Unable to preload CSS") ||
      error.message.includes("Failed to fetch dynamically imported module")
    ) {
      return { hasError: true }
    }
  }

  componentDidCatch(error: Error) {
    if (
      error.message.includes("Unable to preload CSS") ||
      error.message.includes("Failed to fetch dynamically imported module")
    ) {
      if (config.isProductionEnvironment) {
        captureException(error)
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen w-screen items-center justify-center">
          <EmptyState illustrationSrc={EmptyState.Illustration.tools}>
            <EmptyState.Heading>We couldn&lsquo;t load this page</EmptyState.Heading>
            <EmptyState.Content>
              <div className="flex flex-col items-center space-y-4">
                <p className="max-w-sm text-center">
                  Sorry, looks like something went wrong on our&nbsp;end. Please try reloading the
                  page.
                </p>
                <Button onClick={() => location.reload()}>Reload</Button>
              </div>
            </EmptyState.Content>
          </EmptyState>
        </div>
      )
    }

    return this.props.children
  }
}
